/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-console */
import { makeStyles } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { isAuthenticatedState, showPricesState } from '../../../api/appState';
import { CatalogueProduct, productVariantsQuery } from '../../../api/products/catalogue';
import { showStockAvailability } from '../../../config';
import { isProductCanceled } from '../../../helpers/isProductCanceled';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import CataloguePageListContentItemRowAddToCart from './CataloguePageListContentItemRowAddToCart';
import CataloguePageListContentItemRowImage from './CataloguePageListContentItemRowImage';
import CataloguePageListContentItemRowName from './CataloguePageListContentItemRowName';
import CataloguePageListContentItemRowStockInfo from './CataloguePageListContentItemRowStockInfo';
import CataloguePageListContentItemRowPrice from './CataloguePageListContentItemRowPrice';

type CataloguePageListContentItemWarningClassKey = 'root';
interface CataloguePageListContentItemWarningProps
  extends StandardDivProps<CataloguePageListContentItemWarningClassKey> {
  classes?: Record<'root', string>;
  className?: string;
  handleShowVariants?: any;
  product: CatalogueProduct;
  showVariants?: boolean;
  type: 'detail' | 'grid';
}

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.typography.caption,
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(1),
    alignItems: 'flex-end',
  },
  button: {
    ...theme.typography.caption,
    color: theme.palette.warning.main,
    cursor: 'pointer',
    outline: 'inherit',
    background: 'none',
    border: 'none',
    paddingLeft: 0,
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    textDecoration: 'underline',
    fontSize: '0.9rem',
  },
  variants: {
    border: '1px solid #dcdcdcd6',
    padding: '0.75rem',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  product: {
    '&:not(:last-child)': {
      paddingBottom: '1rem',
      borderBottom: '1px solid #0000001f',
    },
    '&:only-child': {
      paddingBottom: '0rem',
      borderBottom: 'none',
    },
  },
  wrapper: {
    color: 'black',
    display: 'grid',
    gridTemplateColumns: '96px auto',
    gridTemplateAreas: `
        "image name      "
        "image name      "
        "stockInfo  stockInfo"
        "price price"
        "addToCart  addToCart"
      `,
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '96px auto max-content',
      gridTemplateAreas: `
        "image name       stockInfo"
        "image name       price"
        "image name addToCart"
      `,
    },
  },
  image: {
    gridArea: 'image',
    width: theme.spacing(12),
    maxHeight: theme.spacing(13.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),

    // Fixes grid in IE 10+
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      '-ms-grid-row': 1,
      '-ms-grid-column': 1,
      '-ms-grid-row-span': 2,
    },
  },
  name: {
    gridArea: 'name',
    borderBottom: 'none',
    // Fixes grid in IE 10+
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      '-ms-grid-row': 1,
      '-ms-grid-column': 1,
      // '-ms-grid-column-span': 2,
    },
  },
  parameters: {
    // Fix grid in IE 10+
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      '-ms-grid-row': 2,
      '-ms-grid-column': 2,
    },
    paddingLeft: '12px',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  stockInfo: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('xs')]: {
      paddingTop: theme.spacing(2),
    },
    // Fix grid in IE 10+
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      '-ms-grid-row': 2,
      '-ms-grid-column': 3,
    },
  },
  price: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),

    // Fix grid in IE 10+
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      '-ms-grid-row': 1,
      '-ms-grid-column': 4,
    },
  },
  addToCart: {
    marginLeft: 'auto',
    gridArea: 'addToCart',

    // Fix grid in IE 10+
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      '-ms-grid-row': 1,
      '-ms-grid-column': 5,
      '-ms-grid-row-span': 2,
    },
  },
  sku: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const CataloguePageListContentItemWarning: React.FC<CataloguePageListContentItemWarningProps> = ({
  classes: pClasses,
  className,
  product,
  type,
}) => {
  const classes = useStyles({ classes: pClasses, className });
  const warningText = product.taxonomies?.includes(`/masterProduct`);
  const [showVariants, setShowVariants] = useState(false);
  const handleShowVariants = React.useCallback(() => {
    setShowVariants((prev) => !prev);
  }, []);

  return warningText ? (
    <React.Suspense fallback={<></>}>
      <div className={clsx(classes.root, className)}>
        <CataloguePageListContentItemWarningButton
          handleShowVariants={handleShowVariants}
          showVariants={showVariants}
          type={type}
        />
        {showVariants ? <CatalogueListContentItemWarningVariants product={product} /> : null}
      </div>
    </React.Suspense>
  ) : null;
};

const CataloguePageListContentItemWarningButton: React.FC<{
  handleShowVariants: () => void;
  showVariants: boolean;
  type: 'detail' | 'grid';
}> = ({ handleShowVariants, showVariants, type }) => {
  const classes = useStyles();
  const t = useTranslateWithFallback();

  return type === 'detail' ? (
    <button className={classes.button} onClick={handleShowVariants}>
      <InfoIcon fontSize='small' />
      {showVariants ? t('cataloguePage/warningButton/hideProducts') : t('cataloguePage/warningButton/showProducts')}
    </button>
  ) : (
    <>{t('cataloguePage/warningButton/otherOptions')}</>
  );
};

const CatalogueListContentItemWarningVariants: React.FC<{ product: CatalogueProduct }> = ({ product }) => {
  const classes = useStyles();
  const variants = useRecoilValue(productVariantsQuery(product.extra?.generatedId));
  const t = useTranslateWithFallback();
  const isAuthenticated = useRecoilValue(isAuthenticatedState);
  const showPrices = useRecoilValue(showPricesState);
  return (
    <div className={classes.variants}>
      {variants.rows.slice(1).map((variant, index) => {
        const variantIsCanceled = isProductCanceled(variant.taxonomies);
        return (
          <div key={index} className={classes.product}>
            <div className={classes.wrapper}>
              <CataloguePageListContentItemRowImage className={classes.image} product={product} />
              <div className={classes.name}>
                <CataloguePageListContentItemRowName product={variant} />
                <span className={classes.parameters}>
                  {t('common/code')}: {variant.sku || ''}
                </span>
              </div>
              {isAuthenticated && variantIsCanceled === false && (
                <>
                  {showStockAvailability && (
                    <CataloguePageListContentItemRowStockInfo className={classes.stockInfo} product={variant} />
                  )}
                  {showPrices && <CataloguePageListContentItemRowPrice className={classes.price} product={variant} />}
                  <CataloguePageListContentItemRowAddToCart className={classes.addToCart} product={variant} />
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CataloguePageListContentItemWarning;
